import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import type { OutletDirectoryRow } from '@kilat/shared';
import { ToggleRow, backLinkStyle } from '@kilat/ui';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX, sectionGridStyle } from '../lib/layout.js';
import {
  outletDirectoryRepo,
  subscribeDirectoryForBrand,
} from '../lib/repositories/outlet-directory-repo.js';
import { directoryCurationRepo } from '../lib/repositories/directory-curation-repo.js';
import { tenantsRepo } from '../lib/repositories/tenants-repo.js';

/**
 * /superadmin/directory — curate the public find directory: spotlight
 * (featured), hide, and record an internal curation note per row.
 *
 * Writes are direct client updates — firestore.rules gates the collection to
 * isSuperAdmin(). Hide is DURABLE, source of truth = the PRIVATE
 * `directoryCuration/{outletId}.hidden` doc: the toggle writes it first, then
 * patches the public row for immediate effect. The row alone can't carry the
 * hide — an operator can force the row to be deleted+rebuilt (opt-in cycle /
 * outlet status flip), which would erase a row-borne flag; the sync trigger
 * re-reads the curation doc on every rebuild instead (security gate,
 * 2026-07-16). The reason note lives on the same private doc.
 *
 * Recognition (growth §09) — verified / founding — is a TENANT-level trust
 * grant, so its source of truth is the tenant's `profile.recognition` (not the
 * row). Toggling writes the tenant flag first (durable; `buildDirectoryRow`
 * re-derives it on every re-projection), then patches every public row of that
 * tenant for immediate effect — the directorySync trigger only fires on
 * branding / outlet writes, so without the patch the badge would lag until the
 * operator next edited an outlet. NON-money: recognition never touches billing.
 */

type ToggleField = 'featured' | 'listedInFindDirectory';
type RecognitionField = 'verified' | 'founding';

export function SuperadminDirectoryPage() {
  const { claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const { t } = useI18n();
  const isSuper = useMemo(() => isSuperAdminForBrand(claims, brand.id), [claims, brand.id]);

  const [saving, setSaving] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const subscribe = useMemo(() => {
    if (!isSuper) return null;
    return (cb: (rows: OutletDirectoryRow[]) => void) => subscribeDirectoryForBrand(brand.id, cb);
  }, [isSuper, brand.id]);
  const {
    data: rows,
    loading,
    error,
  } = useTrackedSnapshot<OutletDirectoryRow[]>(['superadmin-directory', brand.id], subscribe);

  if (authLoading) {
    return (
      <main style={pageStyle}>
        <p role="status" style={mutedStyle}>
          {t('superadmin.directory.loading')}
        </p>
      </main>
    );
  }

  if (!isSuper) {
    return (
      <main style={pageStyle}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h1)' }}>
          {t('superadmin.directory.guardTitle')}
        </h1>
        <p style={mutedStyle}>{t('superadmin.directory.guardBody', { brand: brand.name })}</p>
        <Link to="/queue" style={linkStyle}>
          {t('branding.hours.backToQueue')}
        </Link>
      </main>
    );
  }

  async function toggle(row: OutletDirectoryRow, field: ToggleField, next: boolean) {
    const key = `${row.id}:${field}`;
    setSaving(key);
    setRowError(null);
    try {
      // Hiding writes the SOURCE-OF-TRUTH private curation doc first (it
      // survives operator-forced row rebuilds — security review 2026-07-16),
      // then patches the public row for immediate effect.
      if (field === 'listedInFindDirectory') {
        await directoryCurationRepo.setHidden(row.id, !next);
        await outletDirectoryRepo.update(row.id, {
          listedInFindDirectory: next,
          curatedHidden: !next,
        });
      } else {
        await outletDirectoryRepo.update(row.id, { [field]: next });
      }
    } catch (e) {
      logger.error('directory curation toggle failed', {
        outletId: row.id,
        field,
        error: String(e),
      });
      setRowError(row.id);
    } finally {
      setSaving(null);
    }
  }

  async function toggleRecognition(
    row: OutletDirectoryRow,
    field: RecognitionField,
    next: boolean,
  ) {
    const key = `${row.id}:${field}`;
    setSaving(key);
    setRowError(null);
    try {
      // Source of truth is the TENANT flag (durable across row rebuilds).
      // Write it first, then patch every public row of this tenant so the
      // badge appears immediately (directorySync only re-projects on branding/
      // outlet writes, never on this tenant-profile write).
      await tenantsRepo.update(row.tenantId, { [`profile.recognition.${field}`]: next });
      const siblings = (rows ?? []).filter((r) => r.tenantId === row.tenantId);
      const patchTargets = siblings.length > 0 ? siblings : [row];
      await Promise.all(
        patchTargets.map((r) => outletDirectoryRepo.update(r.id, { [field]: next })),
      );
    } catch (e) {
      logger.error('recognition toggle failed', {
        outletId: row.id,
        tenantId: row.tenantId,
        field,
        error: String(e),
      });
      setRowError(row.id);
    } finally {
      setSaving(null);
    }
  }

  return (
    <main style={pageStyle}>
      <header style={{ marginBottom: 'var(--space-6)' }}>
        <p style={eyebrowStyle}>{t('superadmin.directory.eyebrow')}</p>
        <h1 style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--text-h1)' }}>
          {t('superadmin.directory.title')}
        </h1>
        <p style={{ ...mutedStyle, maxWidth: 620 }}>{t('superadmin.directory.subtitle')}</p>
        <Link to="/queue" style={linkStyle}>
          {t('branding.hours.backToQueue')}
        </Link>
      </header>

      {loading && (
        <p role="status" style={mutedStyle}>
          {t('superadmin.directory.loading')}
        </p>
      )}

      {error && (
        <div role="alert" style={{ maxWidth: 480 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--text-h2)' }}>
            {t('superadmin.directory.errorHeading')}
          </h2>
          <p style={mutedStyle}>{t('superadmin.directory.errorBody')}</p>
        </div>
      )}

      {!loading && !error && (rows?.length ?? 0) === 0 && (
        <div style={{ maxWidth: 520 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--text-h2)' }}>
            {t('superadmin.directory.emptyHeading')}
          </h2>
          <p style={mutedStyle}>{t('superadmin.directory.emptyBody')}</p>
        </div>
      )}

      {(rows?.length ?? 0) > 0 && (
        // One card per outlet, as many abreast as fit at 27.5rem each.
        <ul style={{ ...sectionGridStyle, margin: 0, padding: 0, listStyle: 'none' }}>
          {(rows ?? []).map((row) => (
            <li key={row.id} style={cardStyle}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--space-2)',
                  alignItems: 'baseline',
                }}
              >
                <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', overflowWrap: 'anywhere' }}>
                  {row.name}
                </h2>
                <span style={{ ...mutedStyle, margin: 0, fontFamily: 'monospace', fontSize: 12 }}>
                  {`/o/${row.directorySlug} · ${row.tenantId}`}
                </span>
              </div>
              <p style={{ ...mutedStyle, margin: 'var(--space-1) 0 0' }}>{row.address}</p>

              <ToggleRow
                title={t('superadmin.directory.featuredTitle')}
                detail={t('superadmin.directory.featuredDetail')}
                on={row.featured}
                busy={saving === `${row.id}:featured`}
                disabled={saving !== null}
                onToggle={(next) => void toggle(row, 'featured', next)}
                onLabel={t('superadmin.directory.on')}
                offLabel={t('superadmin.directory.off')}
                busyLabel={t('superadmin.directory.saving')}
              />
              <ToggleRow
                title={t('superadmin.directory.verifiedTitle')}
                detail={t('superadmin.directory.verifiedDetail')}
                on={row.verified}
                busy={saving === `${row.id}:verified`}
                disabled={saving !== null}
                onToggle={(next) => void toggleRecognition(row, 'verified', next)}
                onLabel={t('superadmin.directory.on')}
                offLabel={t('superadmin.directory.off')}
                busyLabel={t('superadmin.directory.saving')}
              />
              <ToggleRow
                title={t('superadmin.directory.foundingTitle')}
                detail={t('superadmin.directory.foundingDetail')}
                on={row.founding}
                busy={saving === `${row.id}:founding`}
                disabled={saving !== null}
                onToggle={(next) => void toggleRecognition(row, 'founding', next)}
                onLabel={t('superadmin.directory.on')}
                offLabel={t('superadmin.directory.off')}
                busyLabel={t('superadmin.directory.saving')}
              />
              <ToggleRow
                title={t('superadmin.directory.listedTitle')}
                detail={t('superadmin.directory.listedDetail')}
                on={row.listedInFindDirectory}
                busy={saving === `${row.id}:listedInFindDirectory`}
                disabled={saving !== null}
                onToggle={(next) => void toggle(row, 'listedInFindDirectory', next)}
                onLabel={t('superadmin.directory.on')}
                offLabel={t('superadmin.directory.off')}
                busyLabel={t('superadmin.directory.saving')}
              />
              <CurationNote row={row} />
              {rowError === row.id && (
                <p
                  role="alert"
                  style={{
                    margin: 'var(--space-2) 0 0',
                    color: 'var(--color-accent)',
                    fontSize: 'var(--text-meta)',
                  }}
                >
                  {t('superadmin.directory.toggleError')}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

/**
 * Per-row internal note — stored in the PRIVATE directoryCuration collection,
 * never on the world-readable public row. Loads once on mount; saves on blur
 * (only when changed); shows an explicit saved/failed state so the curator
 * never wonders whether it stuck.
 */
function CurationNote({ row }: { readonly row: OutletDirectoryRow }) {
  const { t } = useI18n();
  const [loaded, setLoaded] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');

  useEffect(() => {
    let cancelled = false;
    directoryCurationRepo
      .get(row.id)
      .then((doc) => {
        if (cancelled) return;
        const note = doc?.note ?? '';
        setLoaded(note);
        setDraft(note);
      })
      .catch((e: unknown) => {
        logger.error('curation note load failed', { outletId: row.id, error: String(e) });
        if (!cancelled) setLoaded('');
      });
    return () => {
      cancelled = true;
    };
  }, [row.id]);

  async function save() {
    if (loaded === null || draft === loaded) return;
    setState('saving');
    try {
      await directoryCurationRepo.saveNote(row.id, draft);
      setLoaded(draft.slice(0, 300));
      setState('saved');
    } catch (e) {
      logger.error('curation note save failed', { outletId: row.id, error: String(e) });
      setState('failed');
    }
  }

  const noteId = `curation-note-${row.id}`;
  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <label
        htmlFor={noteId}
        style={{
          display: 'block',
          fontSize: 'var(--text-meta)',
          fontWeight: 600,
          marginBottom: 'var(--space-1)',
        }}
      >
        {t('superadmin.directory.noteLabel')}
      </label>
      <input
        id={noteId}
        type="text"
        value={draft}
        maxLength={300}
        placeholder={t('superadmin.directory.notePlaceholder')}
        onChange={(e) => {
          setDraft(e.target.value);
          setState('idle');
        }}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        style={{
          width: '100%',
          minHeight: 'var(--touch-target)',
          padding: '0 var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-outline, var(--color-border-strong))',
          background: 'var(--color-surface-raised, transparent)',
          color: 'var(--color-on-surface)',
          fontSize: 'var(--text-body)',
        }}
      />
      <p
        role="status"
        aria-live="polite"
        style={{
          margin: 'var(--space-1) 0 0',
          fontSize: 'var(--text-meta)',
          color: state === 'failed' ? 'var(--color-accent)' : 'var(--color-on-surface-muted)',
          minHeight: '1.2em',
        }}
      >
        {state === 'saving' && t('superadmin.directory.noteSaving')}
        {state === 'saved' && t('superadmin.directory.noteSaved')}
        {state === 'failed' && t('superadmin.directory.noteFailed')}
        {state === 'idle' &&
          loaded !== null &&
          draft !== loaded &&
          t('superadmin.directory.noteUnsaved')}
      </p>
    </div>
  );
}

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return sa[brandId] === true;
}

const pageStyle = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: 'var(--space-6) var(--space-5) var(--space-16)',
} as const;

const eyebrowStyle = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-accent)',
} as const;

const mutedStyle = {
  margin: 'var(--space-2) 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
} as const;

const linkStyle = {
  // `inline-block` here made `min-height` inert, so this measured 20px tall on a
  // phone. `backLinkStyle` is inline-flex, which is what lets the target grow.
  ...backLinkStyle,
  marginTop: 'var(--space-3)',
  color: 'var(--color-primary)',
  fontWeight: 600,
} as const;

const cardStyle = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-1)',
  padding: 'var(--space-4) var(--space-5)',
} as const;
